/* eslint-disable import/prefer-default-export */
import React, {
  useMemo, useState, useEffect, useCallback, useRef
} from 'react';
import { useForm, useWatch, useFieldArray } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { webhookSchema } from '../schemas/webhookSchema';
import CustomerModel from '../../../../components/Customers/CustomerModel';
import WebhookModelMC from '../../../models/WebhookModelMC';
import ShopOrderModelMC from '../../../models/ShopOrderModelMC';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';

/**
 * useWebhookForm - Custom hook for managing the webhook form logic.
 *
 * @returns {Object} - Form methods, options, and handlers.
 */
export const useWebhookForm = ({ webhookToEdit, onSuccess } = {}) => {
  const { t } = useTranslation('WebhooksScreenMC');
  const dispatch = useDispatch();
  const [view, setView] = useState('form'); // 'form' or 'jsonEditor'
  const [customerColumns, setCustomerColumns] = useState([]);
  const [orderColumns, setOrderColumns] = useState([]);
  const [showVariables, setShowVariables] = useState(false);
  const [originalHeaders, setOriginalHeaders] = useState([]);
  const [originalJsonBody, setOriginalJsonBody] = useState('');

  useEffect(() => {
    CustomerModel.getCustomerColumns().then((res) => {
      setCustomerColumns([...res.fields, ['Mercately_ID', 'web_id'], ['ZOHO ID', 'zoho_agent_id']]);
    });

    ShopOrderModelMC.getFields().then((res) => {
      setOrderColumns(res.fields);
    });
  }, []);

  const eventTypeOptions = useMemo(() => [
    { id: 'contact', label: t('event_type_customers'), icon: 'person-outline' },
    { id: 'order', label: t('event_type_orders'), icon: 'shopping-cart-outline' },
    { id: 'flow', label: t('event_type_flows'), icon: 'file-text-outline' }
  ], [t]);

  const flowColumns = useMemo(() => [
    [t('flow_variable_id'), 'flow_id'],
    [t('flow_variable_name'), 'flow_name'],
    [t('flow_variable_meta_id'), 'meta_flow_id'],
    [t('flow_variable_created_at'), 'created_at'],
    [t('flow_variable_customer_id'), 'customer_id'],
    [t('flow_variable_customer_name'), 'customer_name'],
    [t('flow_variable_customer_phone'), 'customer_phone'],
    [t('flow_variable_customer_email'), 'customer_email'],
    [t('flow_variable_answers'), 'answers'],
    [t('flow_variable_file_field'), 'files.field'],
    [t('flow_variable_file_filename'), 'files.filename'],
    [t('flow_variable_file_url'), 'files.url'],
    [t('flow_variable_file_content_type'), 'files.content_type']
  ], [t]);



  const defaultValues = useMemo(() => ({
    name: '',
    target_url: '',
    isActive: true,
    event_type: '',
    events: [],
    jsonBody: '',
    headers: [],
    auth: {
      type: 'none',
      config: {}
    }
  }), []);

  const values = useMemo(() => {
    if (webhookToEdit) {
      return {
        name: webhookToEdit.name || '',
        target_url: webhookToEdit.target_url || '',
        isActive: webhookToEdit.active !== undefined ? webhookToEdit.active : true,
        event_type: webhookToEdit.event_type || '',
        events: webhookToEdit.events || [],
        jsonBody: webhookToEdit.payload ? JSON.stringify(webhookToEdit.payload, null, 2) : '',
        headers: webhookToEdit.headers ? Object.entries(webhookToEdit.headers).map(([key, value]) => ({ key, value })) : [],
        auth: webhookToEdit.auth || { type: 'none', config: {} }
      };
    }
    return defaultValues;
  }, [webhookToEdit, defaultValues]);

  const methods = useForm({
    resolver: zodResolver(webhookSchema(t)),
    shouldUnregister: false,
    values
  });

  const onSubmit = async (data) => {
    const currentJsonBody = data.jsonBody || methods.getValues('jsonBody');
    const headersArray = data.headers || methods.getValues('headers');
    let parsedPayload = {};
    let parsedHeaders = {};

    if (currentJsonBody) {
      try {
        parsedPayload = JSON.parse(currentJsonBody);
      } catch (e) {
        console.error('JSON Parse Error:', e);
        if (window.showtoast) window.showtoast(t('json_invalid_error'));
        return;
      }
    }

    if (headersArray && headersArray.length > 0) {
      headersArray.forEach((h) => {
        if (h.key) {
          parsedHeaders[h.key] = h.value || '';
        }
      });
    }

    try {
      const apiData = {
        name: data.name,
        target_url: data.target_url,
        active: data.isActive,
        event_type: data.event_type,
        events: data.events,
        payload: parsedPayload,
        headers: parsedHeaders,
        auth: data.auth,
        ...(webhookToEdit?.id && { id: webhookToEdit.id })
      };

      await WebhookModelMC.save(apiData);

      dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
      if (window.showtoast) window.showtoast(t(webhookToEdit ? 'webhook_updated_success' : 'webhook_created_success'));
      if (onSuccess) onSuccess();
    } catch (error) {
      if (window.showtoast) window.showtoast(error || t('webhook_saving_error'));
    }
  };

  const handleEventToggle = (eventId, checked, onChange, currentEvents) => {
    if (checked) {
      onChange([...currentEvents, eventId]);
    } else {
      onChange(currentEvents.filter((id) => id !== eventId));
    }
  };

  const handleEditJson = useCallback(() => {
    setOriginalJsonBody(methods.getValues('jsonBody'));
    setView('jsonEditor');
  }, [methods]);

  const handleSaveJson = useCallback(() => {
    setView('form');
  }, []);

  const handleCancelJson = useCallback(() => {
    methods.setValue('jsonBody', originalJsonBody);
    setView('form');
  }, [methods, originalJsonBody]);

  const handleRemoveJson = useCallback(() => {
    methods.setValue('jsonBody', '');
  }, [methods]);

  const handleEditHeaders = useCallback(() => {
    setOriginalHeaders(methods.getValues('headers') || []);
    setView('headerEditor');
  }, [methods]);

  const handleSaveHeaders = useCallback(async () => {
    const isValid = await methods.trigger('headers');
    if (isValid) {
      const currentHeaders = methods.getValues('headers');
      const sanitizedHeaders = (currentHeaders || []).map((h) => ({
        key: h.key?.trim(),
        value: h.value?.trim()
      }));
      methods.setValue('headers', sanitizedHeaders);
      setView('form');
    }
  }, [methods]);

  const handleCancelHeaders = useCallback(() => {
    methods.setValue('headers', originalHeaders);
    setView('form');
  }, [methods, originalHeaders]);

  const handleRemoveHeaders = useCallback(() => {
    methods.setValue('headers', []);
  }, [methods]);

  const eventType = useWatch({
    control: methods.control,
    name: 'event_type'
  });

  const prevEventTypeRef = useRef(eventType);

  useEffect(() => {
    if (prevEventTypeRef.current !== eventType && prevEventTypeRef.current !== undefined) {
      methods.setValue('events', []);
      methods.setValue('jsonBody', '');
      methods.setValue('headers', []);
    }
    prevEventTypeRef.current = eventType;
  }, [eventType, methods]);

  const availableEvents = useMemo(() => {
    if (eventType === 'order') {
      return [
        { id: 'order.created', label: t('order_created_label'), description: t('order_created_description') },
        { id: 'order.updated', label: t('order_updated_label'), description: t('order_updated_description') }
      ];
    }
    if (eventType === 'contact') {
      return [
        { id: 'customer.created', label: t('customer_created_label'), description: t('customer_created_description') },
        { id: 'customer.updated', label: t('customer_updated_label'), description: t('customer_updated_description') }
      ];
    }
    if (eventType === 'flow') {
      return [
        { id: 'flow.received', label: t('flow_received_label'), description: t('flow_received_description') }
      ];
    }
    return [];
  }, [eventType, t]);

  const { fields: headerFields, append: appendHeader, remove: removeHeader } = useFieldArray({
    control: methods.control,
    name: 'headers'
  });

  const toggleVariables = useCallback(() => {
    setShowVariables((prev) => !prev);
  }, []);

  const availableColumns = useMemo(() => {
    if (eventType === 'order') return orderColumns || [];
    if (eventType === 'contact') return customerColumns || [];
    if (eventType === 'flow') return flowColumns || [];
    return [];
  }, [eventType, orderColumns, customerColumns, flowColumns]);

  const variableOptions = useMemo(() => availableColumns.map((col) => ({
    id: col[1],
    label: (
      <span>
        {col[0]}
        {' '}
        <span className="tw-text-gray-400 tw-text-xs">{`{{${col[1]}}}`}</span>
      </span>
    ),
    textLabel: `${col[0]} {{${col[1]}}}`
  })), [availableColumns]);

  const filterVariables = useCallback((searchTerm) => {
    if (!searchTerm) return variableOptions;
    const lowerTerm = searchTerm.toLowerCase();
    return variableOptions.filter((opt) => opt.textLabel.toLowerCase().includes(lowerTerm));
  }, [variableOptions]);

  return {
    methods,
    onSubmit: methods.handleSubmit(onSubmit),
    eventType,
    eventTypeOptions,
    availableEvents,
    handleEventToggle,
    view,
    showVariables,
    variableOptions,
    toggleVariables,
    filterVariables,
    handleEditJson,
    handleSaveJson,
    handleCancelJson,
    handleRemoveJson,
    handleEditHeaders,
    handleSaveHeaders,
    handleCancelHeaders,
    handleRemoveHeaders,
    availableColumns: availableColumns,
    headerFields,
    appendHeader,
    removeHeader,
    loading: methods.formState.isSubmitting,
    t
  };
};
